import { useState, useEffect } from 'react';
import {
  useCachedBusinessInsights,
  useCachedSelectedProject,
  useTranslation,
} from 'hooks';
import { Card } from 'shared/ui/card/card';
import { SkeletonList } from 'shared/ui/skeleton/skeleton-list';
import { RulesPurpose } from 'shared/ui/rules-purpose/rules-purpose';
import { Drawer } from 'shared/ui/drawer/drawer';
import { ProductRules } from 'widgets/product-rules/product-rules';
import { useRuleStats } from 'shared/hooks/useRulesStat';
import { ImpactDrawerContent } from 'widgets/technical-rules/components/impact-drawer-content/impact-drawer-content';
import { ImprovementTrialBanner } from 'widgets/improvement-trial-banner/improvement-trial-banner';
import { ImprovementSectionHeader } from 'widgets/improvement-section-header/improvement-section-header';
import cls from './product-rules-block.module.scss';
import { InsightsService } from 'store/api/apiTypes';
import { useReportProgress } from 'shared/hooks/useReportProgress';
import { getSectionStatus } from 'shared/helpers/runSectionStatus';

export const ProductRulesBlock: React.FC = () => {
  const { t } = useTranslation();
  const { selectedProject, isTrialExpired } = useCachedSelectedProject();
  const { services, isLoading } = useCachedBusinessInsights();
  const { productsStat, productsLoading } = useRuleStats();
  const [activeProduct, setActiveProduct] = useState<InsightsService>();
  const [isImpactDrawerOpen, setIsImpactDrawerOpen] = useState(false);
  const { blocks, isRunActive, allComplete } = useReportProgress();

  const productsSectionStatus = getSectionStatus([
    blocks.conversationalWriting,
    blocks.statisticalEvidence,
    blocks.contentFreshness,
  ]);
  const tabsVisible = !isRunActive || blocks.businessInsights === 'ready';

  useEffect(() => {
    if (services && services.length > 0 && !activeProduct) {
      setActiveProduct(services[0]);
    }
  }, [activeProduct, services]);

  if (!selectedProject) {
    return (
      <>
        <SkeletonList count={3} height={132} />
      </>
    );
  }

  return (
    <Card className={cls.productsCard}>
      <ImprovementSectionHeader
        title={t('productsAndServices')}
        stats={productsStat}
        isLoading={isLoading || productsLoading}
        isTrialExpired={isTrialExpired}
        runStatus={isRunActive ? productsSectionStatus : null}
      />

      <div className={cls.productsContainer}>
        <div className={cls.rulesSection}>
          <div className={cls.productTabs}>
            {isRunActive ? (
              tabsVisible &&
              services?.map(s => {
                const isActive = s.name === activeProduct?.name;
                return (
                  <div
                    key={s.name}
                    onClick={() => setActiveProduct(s)}
                    className={`${cls.productTab} ${isActive ? cls.active : ''}`}
                  >
                    <span className={cls.productName}>{s.name}</span>
                    <p className={cls.productMeta}>
                      {s.geographic_focus}
                      <br />
                      {s.target_audience}
                    </p>
                  </div>
                );
              })
            ) : isLoading ? (
              <>
                <SkeletonList count={3} height={60} width={200} />
              </>
            ) : (
              services?.map(s => {
                const isActive = s.name === activeProduct?.name;
                return (
                  <div
                    key={s.name}
                    onClick={() => setActiveProduct(s)}
                    className={`${cls.productTab} ${isActive ? cls.active : ''}`}
                  >
                    <span className={cls.productName}>{s.name}</span>
                    <p className={cls.productMeta}>
                      {s.geographic_focus}
                      <br />
                      {s.target_audience}
                    </p>
                  </div>
                );
              })
            )}
          </div>

          <div className={cls.rulesList}>
            <ProductRules
              productName={activeProduct?.name || ''}
              productId={activeProduct?.unique_id}
              color='white'
              showScore={false}
              showBanner={false}
              productNumber={
                services?.findIndex(s => s.name === activeProduct?.name) + 1
              }
              useImprovementVersion={true}
            />
          </div>
          <ImprovementTrialBanner />
        </div>

        <div className={cls.purposeSection}>
          <RulesPurpose
            purposeText={t('productsPurposeDescription')}
            implementedText={t('productsPurposeImplemented')}
            notImplementedText={t('productsPurposeNotImplemented')}
            onViewFullImpact={() => setIsImpactDrawerOpen(true)}
            showViewFullImpact={!isRunActive || allComplete}
          />
        </div>
      </div>

      <Drawer
        isOpen={isImpactDrawerOpen}
        onClose={() => setIsImpactDrawerOpen(false)}
        title={`${t('productsAndServices')} ${t('fullImpact')}`}
      >
        <ImpactDrawerContent showBusinessProfile={false} />
      </Drawer>
    </Card>
  );
};
